import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiDownload, FiShare2, FiEdit2, FiTrash2, FiClock, 
  FiUser, FiFolder, FiTag, FiEye, FiEyeOff 
} from 'react-icons/fi';
import documentService from '../../services/documentService';

const DocumentViewer = ({ documentId, onDelete, onEdit }) => {
  const [document, setDocument] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVersions, setShowVersions] = useState(false);

  useEffect(() => {
    if (documentId) {
      loadDocument();
    }
  }, [documentId]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      const data = await documentService.getById(documentId);
      setDocument(data.data);
      if (data.data.versionHistory) {
        setVersionHistory(data.data.versionHistory);
      }
    } catch (error) {
      console.error('Error loading document:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const data = await documentService.download(documentId);
      window.open(data.data.file_url, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const handleShare = async () => {
    try {
      await documentService.share(documentId, {
        is_public: !document.is_public
      });
      loadDocument();
    } catch (error) {
      console.error('Error sharing document:', error);
      alert('Failed to update sharing settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading document...</div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Document not found</p>
      </div>
    );
  }

  const canPreview = documentService.canPreview(document.file_type);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="text-5xl">
              {documentService.getFileTypeIcon(document.file_type)}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {document.title}
              </h1>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span className={`px-2 py-1 rounded ${
                  documentService.getFileTypeColor(document.file_type)
                }`}>
                  {document.file_type?.toUpperCase() || 'FILE'}
                </span>
                <span>{documentService.formatFileSize(document.file_size)}</span>
                <span>Version {document.version}</span>
                {document.is_public ? (
                  <span className="flex items-center text-green-600">
                    <FiEye className="mr-1" size={14} />
                    Public
                  </span>
                ) : (
                  <span className="flex items-center text-gray-600">
                    <FiEyeOff className="mr-1" size={14} />
                    Private
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiDownload className="mr-2" size={18} />
              Download
            </button>
            <button
              onClick={handleShare}
              className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FiShare2 className="mr-2" size={18} />
              {document.is_public ? 'Make Private' : 'Make Public'}
            </button>
            {onEdit && (
              <button
                onClick={() => onEdit(document)}
                className="p-2 text-gray-600 hover:text-blue-600 rounded-lg transition-colors"
                title="Edit"
              >
                <FiEdit2 size={20} />
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(document.id)}
                className="p-2 text-gray-600 hover:text-red-600 rounded-lg transition-colors"
                title="Delete"
              >
                <FiTrash2 size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Description */}
        {document.description && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-gray-700">{document.description}</p>
          </div>
        )}
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
          <div className="space-y-3">
            {document.uploader && (
              <div className="flex items-center text-sm">
                <FiUser className="mr-2 text-gray-400" size={16} />
                <span className="text-gray-600">Uploaded by:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {document.uploader.first_name} {document.uploader.last_name}
                </span>
              </div>
            )}
            
            <div className="flex items-center text-sm">
              <FiClock className="mr-2 text-gray-400" size={16} />
              <span className="text-gray-600">Uploaded:</span>
              <span className="ml-2 font-medium text-gray-900">
                {new Date(document.created_at).toLocaleString()}
              </span>
            </div>

            {document.updated_at !== document.created_at && (
              <div className="flex items-center text-sm">
                <FiClock className="mr-2 text-gray-400" size={16} />
                <span className="text-gray-600">Last modified:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {new Date(document.updated_at).toLocaleString()}
                </span>
              </div>
            )}

            {document.folder && (
              <div className="flex items-center text-sm">
                <FiFolder className="mr-2 text-gray-400" size={16} />
                <span className="text-gray-600">Folder:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {document.folder.name}
                </span>
              </div>
            )}

            {document.project && (
              <div className="flex items-center text-sm">
                <span className="text-gray-600">Project:</span>
                <Link
                  to={`/app/projects/${document.project.id}`}
                  className="ml-2 font-medium text-blue-600 hover:text-blue-700"
                >
                  {document.project.name}
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Tags */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
          {document.tags && document.tags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {document.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  <FiTag className="mr-1" size={12} />
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No tags</p>
          )}
        </div>
      </div>

      {/* Preview */}
      {canPreview && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {document.file_type === 'pdf' ? (
              <iframe
                src={document.file_url}
                className="w-full h-[600px]"
                title="Document Preview"
              />
            ) : ['jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp'].includes(document.file_type?.toLowerCase()) ? (
              <img
                src={document.file_url}
                alt={document.title}
                className="w-full h-auto"
              />
            ) : (
              <div className="p-8 text-center text-gray-500">
                <p>Preview not available for this file type</p>
                <button
                  onClick={handleDownload}
                  className="mt-4 text-blue-600 hover:text-blue-700"
                >
                  Download to view
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Version History */}
      {versionHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
            <button
              onClick={() => setShowVersions(!showVersions)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {showVersions ? 'Hide' : 'Show'} ({versionHistory.length})
            </button>
          </div>
          
          {showVersions && (
            <div className="space-y-3">
              {versionHistory.map((version) => (
                <div
                  key={version.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-900">
                      Version {version.version}
                    </span>
                    <span className="text-sm text-gray-600">
                      {documentService.formatFileSize(version.file_size)}
                    </span>
                    <span className="text-sm text-gray-500">
                      {new Date(version.created_at).toLocaleDateString()}
                    </span>
                    {version.uploader && (
                      <span className="text-sm text-gray-600">
                        by {version.uploader.first_name} {version.uploader.last_name}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => window.open(version.file_url, '_blank')}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentViewer;


